import { useRef, useState } from "react";
import {
  Button,
  TextInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

// Workshop snapshots of src/lib/model-registry.ts (2026-09-08).
// Keep mock data local: the production registry depends on companion stores.
const CLAUDE_EFFORTS = ["Low", "Medium", "High", "Xhigh", "Max"];
const CODEX_EFFORTS = [...CLAUDE_EFFORTS, "Ultra"];
interface DemoModel {
  name: string;
  efforts: string[];
  defaultEffort: string;
}
const DEFAULT_MODEL: DemoModel = {
  name: "Sonnet 5",
  efforts: CLAUDE_EFFORTS,
  defaultEffort: "Medium",
};
const PROVIDERS: Record<string, DemoModel[]> = {
  Claude: [
    { name: "Sonnet 5", efforts: CLAUDE_EFFORTS, defaultEffort: "Medium" },
    { name: "Opus 4.8", efforts: CLAUDE_EFFORTS, defaultEffort: "High" },
    {
      name: "Sonnet 4.6",
      efforts: ["Low", "Medium", "High", "Max"],
      defaultEffort: "High",
    },
    { name: "Haiku 4.5", efforts: [], defaultEffort: "Default" },
  ],
  Codex: [
    { name: "GPT-5.6 Terra", efforts: CODEX_EFFORTS, defaultEffort: "Medium" },
    { name: "GPT-5.6 Sol", efforts: CODEX_EFFORTS, defaultEffort: "Low" },
    { name: "GPT-5.6 Luna", efforts: CLAUDE_EFFORTS, defaultEffort: "Medium" },
    {
      name: "GPT-5.5",
      efforts: ["Minimal", "Low", "Medium", "High", "Xhigh"],
      defaultEffort: "Medium",
    },
    {
      name: "GPT-5.4 Nano",
      efforts: ["Minimal", "Low", "Medium", "High"],
      defaultEffort: "Medium",
    },
  ],
  OpenCode: [
    "Qwen 3.8 Flash",
    "Qwen 3.6 Plus",
    "MiniMax M3",
    "GLM 5.1",
    "Kimi K2.5",
    "Gemini 3.1 Pro",
    "Gemini 3 Flash",
  ].map((name) => ({ name, efforts: [], defaultEffort: "Default" })),
};

export function ComposerModelMenu({
  testId,
  disabled,
}: {
  testId: string;
  disabled?: boolean;
}) {
  const [provider, setProvider] = useState("Claude");
  const [browseProvider, setBrowseProvider] = useState("Claude");
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [effort, setEffort] = useState("Medium");
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const modelsRef = useRef<HTMLDivElement>(null);
  const providerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const filteredModels = (PROVIDERS[browseProvider] ?? []).filter((item) =>
    item.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  function selectModel(next: DemoModel) {
    setProvider(browseProvider);
    setModel(next);
    setEffort((previous) =>
      previous === "Default" || next.efforts.includes(previous)
        ? previous
        : next.defaultEffort,
    );
  }
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          setBrowseProvider(provider);
          setSearch("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="composer-control min-w-0 gap-1.5 px-2 text-secondary"
          aria-label={`Model: ${model.name}, ${provider}${model.efforts.length ? `, ${effort} thinking` : ""}`}
          data-testid={`${testId}-model-open`}
        >
          <span className="truncate">{model.name}</span>
          <Icons.ChevronDown className="h-3 w-3 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={12}
        className="dark composer-menu flex h-80 max-h-[var(--radix-dropdown-menu-content-available-height)] w-64 flex-col text-primary"
        data-testid={`${testId}-model-menu`}
      >
        <div
          className="flex shrink-0 gap-1 pb-2"
          role="group"
          aria-label="Providers"
        >
          {Object.keys(PROVIDERS).map((name) => (
            <DropdownMenuItem
              key={name}
              ref={(node) => {
                providerRefs.current[name] = node;
              }}
              role="menuitemradio"
              aria-checked={browseProvider === name}
              className={cn(
                "composer-provider flex-1 justify-center px-2 text-xs",
                browseProvider === name && "bg-glass-fill-heavy text-primary",
              )}
              onSelect={(event) => {
                event.preventDefault();
                setBrowseProvider(name);
                setSearch("");
                if (name === "OpenCode")
                  requestAnimationFrame(() => searchRef.current?.focus());
              }}
              data-testid={`${testId}-provider-${name.toLowerCase()}`}
            >
              {name}
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator className="shrink-0" />
        {browseProvider === "OpenCode" && (
          <div className="shrink-0 px-1 py-2">
            <TextInput
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${browseProvider} models…`}
              aria-label={`Search ${browseProvider} models`}
              data-testid={`${testId}-model-search`}
              className="h-8 text-xs"
              onKeyDown={(event) => {
                if (event.key === "Escape") return;
                event.stopPropagation();
                if (event.key === "ArrowDown" || event.key === "Tab") {
                  event.preventDefault();
                  const firstModel =
                    modelsRef.current?.querySelector<HTMLElement>(
                      '[role="menuitemradio"]',
                    );
                  if (event.shiftKey || !firstModel)
                    providerRefs.current[browseProvider]?.focus();
                  else firstModel.focus();
                }
              }}
            />
          </div>
        )}
        <div
          ref={modelsRef}
          key={`${browseProvider}-${search}`}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 [scrollbar-gutter:stable]"
          data-testid={`${testId}-model-list`}
          role="group"
          aria-label={`${browseProvider} models`}
        >
          {filteredModels.map((item) => (
            <DropdownMenuItem
              key={item.name}
              role="menuitemradio"
              aria-checked={
                provider === browseProvider && model.name === item.name
              }
              onSelect={(event) => {
                event.preventDefault();
                selectModel(item);
              }}
              className="gap-2 rounded-lg py-1.5"
              data-testid={`${testId}-model-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span className="min-w-0 flex-1 text-xs">{item.name}</span>
              {provider === browseProvider && model.name === item.name && (
                <Icons.Check className="h-3.5 w-3.5 text-accent" />
              )}
            </DropdownMenuItem>
          ))}
          {filteredModels.length === 0 && (
            <p role="status" className="px-3 py-4 text-xs text-muted">
              No models found.
            </p>
          )}
        </div>
        <DropdownMenuSeparator className="shrink-0" />
        <div className="h-14 shrink-0 px-2 pt-1">
          <div className="flex items-center justify-between px-1 text-xs">
            <span className="text-muted">Thinking</span>
            <span className="text-primary" aria-live="polite">
              {provider === browseProvider && model.efforts.length > 0
                ? effort
                : "—"}
            </span>
          </div>
          {provider === browseProvider && model.efforts.length > 0 && (
            <div
              className="mt-1 flex items-center gap-1"
              role="group"
              aria-label="Thinking level"
              data-testid={`${testId}-thinking-ticks`}
            >
              {["Default", ...model.efforts].map((level) => (
                <DropdownMenuItem
                  key={level}
                  role="menuitemradio"
                  aria-checked={effort === level}
                  aria-label={`${level} thinking`}
                  title={level}
                  onSelect={(event) => {
                    event.preventDefault();
                    setEffort(level);
                  }}
                  className="composer-thinking-tick min-h-7 min-w-0 flex-1 justify-center rounded-md px-0 py-0"
                  data-testid={`${testId}-effort-${level.toLowerCase()}`}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "w-1 rounded-full transition-[height,background-color]",
                      effort === level ? "h-4 bg-current text-primary" : "h-2.5 bg-current text-muted",
                    )}
                  />
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
